import { X, RotateCcw } from 'lucide-react';
import { SessionPpgData } from '@repo/shared/dtos/devices/SessionPpgDto';
import { SessionByDateDto } from '@repo/shared/dtos/my-insights/SessionByDateDto';
import { ProcessedHeartRateData } from '@repo/shared/dtos/devices/HeartRateDto';
import { HeartRateStats } from '../HeartRateStats';
import { SessionsList } from '../SessionsList';
import { SessionDetails } from '../SessionDetails';
import { OuraHeartRateChart } from '../OuraHeartRateChart';
import { EmptyState } from '../EmptyState';
import { CHART_CONFIG } from '../constants';

interface PopupContentProps {
  heartRateData: {
    data: ProcessedHeartRateData[];
    min: number;
    avg: number;
    max: number;
  };
  sessions: SessionByDateDto[];
  sessionData?: SessionPpgData | null;
  showSessionDetails: boolean;
  showSession: boolean;
  isZoomed: boolean;
  zoomDomain: { xStart: Date; xEnd: Date } | null;
  selectedSessionId?: string;
  onSelectSession: (sessionId: string) => void;
  onCloseSessionDetails: () => void;
  onToggleSession: () => void;
  onZoom: () => void;
  onResetZoom: () => void;
  onMarkerClick: (sessionId: string) => void;
  onZoomChange: (zoomDomain: { xStart: Date; xEnd: Date } | null) => void;
  refetchHeartRate: () => void;
}

export const PopupContent = ({
  heartRateData,
  sessions,
  sessionData,
  showSessionDetails,
  showSession,
  isZoomed,
  zoomDomain,
  selectedSessionId,
  onSelectSession,
  onCloseSessionDetails,
  onToggleSession,
  onZoom,
  onResetZoom,
  onMarkerClick,
  onZoomChange,
  refetchHeartRate,
}: PopupContentProps) => (
  <div className="flex-1 overflow-y-auto p-6">
    {!heartRateData?.data?.length ? (
      <EmptyState refetch={refetchHeartRate} />
    ) : (
      <div className="flex h-full flex-col space-y-6">
        <HeartRateStats min={heartRateData.min} avg={heartRateData.avg} max={heartRateData.max} />

        {sessions.length > 0 && !showSessionDetails && (
          <SessionsList
            sessions={sessions}
            onSelectSession={onSelectSession}
            selectedSessionId={selectedSessionId}
          />
        )}

        {!showSessionDetails && isZoomed && (
          <div className="rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface)] p-3 text-[var(--anl-text)]">
            <div className="flex items-center justify-between">
              <div className="text-sm text-[var(--anl-text)]">
                <span className="font-medium">Zoomed View</span>
                {zoomDomain && (
                  <span className="ml-2 text-[var(--anl-text)]">
                    {zoomDomain.xStart.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}{' '}
                    -{' '}
                    {zoomDomain.xEnd.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                )}
              </div>
              <button
                onClick={onResetZoom}
                className="flex items-center rounded-full bg-[var(--anl-surface-elev)] px-3 py-1 text-xs text-[var(--anl-text)] transition-colors hover:opacity-90"
              >
                <RotateCcw className="mr-1 h-3 w-3" /> Reset Zoom
              </button>
            </div>
          </div>
        )}

        {showSessionDetails && sessionData && (
          <div className="relative">
            <button
              onClick={onCloseSessionDetails}
              className="absolute -right-3 -top-3 z-10 rounded-full bg-[var(--anl-surface-elev)] p-1 text-[var(--anl-text)] shadow-md hover:opacity-90"
            >
              <X className="h-4 w-4" />
            </button>
            <SessionDetails
              sessionData={sessionData}
              showSession={showSession}
              isZoomed={isZoomed}
              onToggleSession={onToggleSession}
              onZoom={onZoom}
              onResetZoom={onResetZoom}
            />
          </div>
        )}

        <div className="relative flex-1 rounded-lg bg-[var(--anl-surface)] p-3 text-[var(--anl-text)] shadow-lg">
          <OuraHeartRateChart
            heartRateData={heartRateData}
            sessionData={showSessionDetails ? sessionData : undefined}
            allSessions={sessions}
            showSession={showSession}
            zoomDomain={zoomDomain}
            colors={CHART_CONFIG}
            onMarkerClick={onMarkerClick}
            onZoomChange={onZoomChange}
          />
        </div>
      </div>
    )}
  </div>
);
